<template>
  <section class="aso-public-entry">
    <div class="aso-public-entry__body">
    <div class="aso-public-entry__card">
      <h1>Assinatura de ASO</h1>
      <p class="aso-public-entry__context">
        <strong>{{ tenantTitleCase }}</strong>
        | Consulta
        <strong>{{ idConsulta }}</strong>
      </p>

      <p v-if="loading" class="aso-public-entry__status">
        Consultando status da assinatura...
      </p>

      <div v-else-if="errorMessage" class="aso-public-entry__status aso-public-entry__status--error">
        {{ errorMessage }}
      </div>

      <div v-else-if="alreadySigned" class="aso-public-entry__signed-card">
        <svg class="aso-public-entry__signed-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 6.41-6.29 6.3a1 1 0 0 1-1.42 0L7 12.41a1 1 0 1 1 1.41-1.41L10 12.59l5.59-5.59A1 1 0 0 1 17 8.41z" fill="#127902"/>
        </svg>
        <h2 class="aso-public-entry__signed-title">{{ justSigned ? 'ASO assinado com sucesso!' : 'O ASO referente a esta consulta já está assinado!' }}</h2>
      </div>

      <div v-else class="aso-public-entry__status aso-public-entry__status--pending">
        <p class="aso-public-entry__subtitle">
          Documento ainda não assinado. Siga as etapas para finalizar o processo.
        </p>

        <section
          v-if="showValidationBoard"
          class="aso-public-entry__validation-board"
          aria-label="Validação de Informações"
        >
          <h2 class="aso-public-entry__validation-title">Validação de Informações</h2>
          <p class="aso-public-entry__validation-subtitle">
            Valide as informações necessárias para finalizar o cadastro
          </p>

          <div class="aso-public-entry__validation-list">
            <article
              class="aso-public-entry__validation-item"
              :class="validationClass('email')"
            >
              <div class="aso-public-entry__validation-icon" aria-hidden="true">
                <email-icon />
              </div>
              <div class="aso-public-entry__validation-copy">
                <h3>Validação de e-mail</h3>
                <p>{{ emailStatusLabel }}</p>
              </div>
              <button
                type="button"
                class="aso-public-entry__validation-action aso-flow-button aso-flow-button--loud aso-flow-button--compact"
                :disabled="busy || !isValidationActionVisible('email')"
                @click="goToValidationStep('email')"
              >
                Validar
              </button>
            </article>

            <article
              class="aso-public-entry__validation-item"
              :class="validationClass('identidade')"
            >
              <div class="aso-public-entry__validation-icon" aria-hidden="true">
                <id-icon />
              </div>
              <div class="aso-public-entry__validation-copy">
                <h3>Validação de identidade</h3>
                <p>{{ identidadeStatusLabel }}</p>
              </div>
              <button
                type="button"
                class="aso-public-entry__validation-action aso-flow-button aso-flow-button--loud aso-flow-button--compact"
                :disabled="busy || !isValidationActionVisible('identidade')"
                @click="goToValidationStep('identidade')"
              >
                Validar
              </button>
            </article>

            <article
              v-if="!cadastroExistente"
              class="aso-public-entry__validation-item"
              :class="validationClass('telefone')"
            >
              <div class="aso-public-entry__validation-icon" aria-hidden="true">
                <phone-icon />
              </div>
              <div class="aso-public-entry__validation-copy">
                <h3>Validação de telefone</h3>
                <p>{{ telefoneStatusLabel }}</p>
              </div>
              <button
                type="button"
                class="aso-public-entry__validation-action aso-flow-button aso-flow-button--loud aso-flow-button--compact"
                :disabled="busy || !isValidationActionVisible('telefone')"
                @click="goToValidationStep('telefone')"
              >
                Validar
              </button>
            </article>

            <article
              v-if="!cadastroExistente"
              class="aso-public-entry__validation-item"
              :class="validationClass('senha')"
            >
              <div class="aso-public-entry__validation-icon" aria-hidden="true">
                <password-icon />
              </div>
              <div class="aso-public-entry__validation-copy">
                <h3>Validação de senha</h3>
                <p>{{ senhaStatusLabel }}</p>
              </div>
              <button
                type="button"
                class="aso-public-entry__validation-action aso-flow-button aso-flow-button--loud aso-flow-button--compact"
                :disabled="busy || !isValidationActionVisible('senha')"
                @click="goToValidationStep('senha')"
              >
                Validar
              </button>
            </article>
          </div>

          <div class="aso-public-entry__validation-footer">
            <button
              type="button"
              class="aso-public-entry__validation-secondary aso-flow-button aso-flow-button--quiet aso-flow-button--compact"
              :disabled="busy"
              @click="resetValidationBoard"
            >
              Limpar
            </button>
            <button
              type="button"
              class="aso-public-entry__validation-primary aso-flow-button aso-flow-button--loud aso-flow-button--compact"
              :disabled="busy || !canAdvanceValidationBoard"
              @click="advanceValidationBoard"
            >
              Finalizar
            </button>
          </div>
        </section>

        <p
          v-if="stepMessage"
          :class="['aso-public-entry__hint', stepError ? 'aso-public-entry__hint--error' : 'aso-public-entry__hint--ok']"
        >
          {{ stepMessage }}
        </p>

        <div v-if="wizardComplete" class="aso-public-entry__status aso-public-entry__status--ok aso-public-entry__status--compact">
          Processo concluído com sucesso. Recarregando status da assinatura...
        </div>

        <div v-else-if="!showValidationBoard">
          <div v-if="currentStep === 1" class="aso-public-entry__step">
            <aso-validar-email-step
              v-if="emailValidationView === 'email'"
              :email="email"
              :busy="busy"
              @update:email="email = $event"
              @continue="enviarTokenEmail"
            />

            <aso-valida-token-email-step
              v-else
              :email="email"
              :values="emailTokenValues"
              :busy="busy"
              :can-resend="tokenExpired"
              :resend-label="tokenResendLabel"
              @digit-input="onEmailTokenDigitInput"
              @digit-keydown="onEmailTokenDigitKeydown"
              @paste="onEmailTokenPaste"
              @change-email="goToEmailForm"
              @confirm="validarToken"
              @resend="reenviarTokenEmail"
            />
          </div>

          <div v-if="currentStep === 2" class="aso-public-entry__step">
            <h2>2. Validação de identidade</h2>

            <aso-validar-identidade-step
              :cpf="cpf"
              :busy="busy"
              :cadastro-existente="cadastroExistente"
              :editavel="cadastroEditavel"
              :nome="nome"
              :masked-nome="cadastroMaskedNome"
              :masked-cpf="cadastroMaskedCpf"
              :sexo="sexo"
              :data-nascimento="dataNascimento"
              @update:cpf="cpf = $event"
              @update:nome="nome = $event"
              @update:sexo="sexo = $event"
              @update:dataNascimento="dataNascimento = $event"
              @toggle-edit="cadastroEditavel = $event; onCadastroEditavelToggle()"
              @validate="cadastroExistente ? confirmarDadosExistente() : validarCpf()"
            />
          </div>

          <div v-if="currentStep === 3" class="aso-public-entry__step">
            <h2>3. Validação de telefone</h2>

            <aso-validar-telefone-step
              v-if="phoneValidationView === 'phone'"
              :telefone="telefone"
              :busy="busy"
              @update:telefone="telefone = $event"
              @continue="enviarTokenTelefone"
            />

            <aso-valida-token-telefone-step
              v-else
              :telefone="telefone"
              :values="phoneTokenValues"
              :busy="busy"
              :can-resend="tokenExpired"
              :resend-label="tokenResendLabel"
              @digit-input="onPhoneTokenDigitInput"
              @digit-keydown="onPhoneTokenDigitKeydown"
              @paste="onPhoneTokenPaste"
              @confirm="validarTokenTelefone"
              @resend="reenviarTokenTelefone"
            />
          </div>

          <div v-if="currentStep === 4" class="aso-public-entry__step">
            <h2>4. Validação de senha</h2>

            <aso-validar-senha-step
              :password="password"
              :password-confirm="passwordConfirm"
              :password-error="passwordConfirmError"
              :busy="busy"
              @update:password="password = $event"
              @update:passwordConfirm="passwordConfirm = $event"
              @confirm-blur="onPasswordConfirmBlur"
              @continue="validarSenha"
            />
          </div>

          <div v-if="currentStep === 5" class="aso-public-entry__step">
            <h2>5. Upload dos documentos</h2>

            <div class="aso-public-entry__form">
              <upload-document-card
                side="frente"
                label="Frente"
                :busy="busy"
                :loading="uploadLoadingFrente"
                :has-error="uploadErrorFrente"
                :uploaded="!!frenteDocumento"
                :disabled="false"
                @selected="onDocumentSelected"
              />

              <upload-document-card
                side="verso"
                label="Verso"
                :busy="busy"
                :loading="uploadLoadingVerso"
                :has-error="uploadErrorVerso"
                :uploaded="!!versoDocumento"
                :disabled="!frenteDocumento"
                @selected="onDocumentSelected"
              />

              <button
                type="button"
                class="aso-public-entry__button aso-flow-button aso-flow-button--loud aso-flow-button--full"
                :disabled="busy || !canSaveDocumentos"
                @click="salvarDocumentos"
              >
                {{ busy ? 'Enviando...' : 'Salvar documentos' }}
              </button>
            </div>
          </div>

          <div v-if="currentStep === 6" class="aso-public-entry__step">
            <h2>6. Assinatura</h2>
            <p class="aso-public-entry__hint">
              Envie uma foto nítida do seu rosto para concluir a assinatura do ASO.
            </p>

            <div class="aso-public-entry__form">
              <upload-face-card
                :busy="busy"
                :loading="uploadLoadingRosto"
                :has-error="uploadErrorRosto"
                :uploaded="!!fotoRosto"
                :disabled="false"
                @selected="onSelectFace"
              />

              <button
                type="button"
                class="aso-public-entry__button aso-flow-button aso-flow-button--loud aso-flow-button--full"
                :disabled="busy"
                @click="salvarAssinatura"
              >
                {{ busy ? 'Finalizando...' : 'Concluir assinatura' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </section>
</template>

<script>
import wizardMixin from './wizard.mixin'
import UploadDocumentCard from './UploadDocumentCard'
import UploadFaceCard from './UploadFaceCard'
import { EmailIcon, IdIcon, PhoneIcon, PasswordIcon } from './ValidationIcons.vue'
import AsoValidarEmailStep from './AsoValidarEmailStep.vue'
import AsoValidaTokenEmailStep from './AsoValidaTokenEmailStep.vue'
import AsoValidarIdentidadeStep from './AsoValidarIdentidadeStep.vue'
import AsoValidarTelefoneStep from './AsoValidarTelefoneStep.vue'
import AsoValidaTokenTelefoneStep from './AsoValidaTokenTelefoneStep.vue'
import AsoValidarSenhaStep from './AsoValidarSenhaStep.vue'

export default {
  name: 'FuncionarioAsoAssinar',
  components: {
    UploadDocumentCard,
    UploadFaceCard,
    EmailIcon,
    IdIcon,
    PhoneIcon,
    PasswordIcon,
    AsoValidarEmailStep,
    AsoValidaTokenEmailStep,
    AsoValidarIdentidadeStep,
    AsoValidarTelefoneStep,
    AsoValidaTokenTelefoneStep,
    AsoValidarSenhaStep,
  },
  mixins: [wizardMixin],
}
</script>

<style src="./index.css"></style>



// WEBPACK FOOTER //
// index.vue?fb89fbc4